/**
 * Module SAV - Fiche detail
 * 13/05/2026 - feature/module-sav
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../services/api';
import { ArrowLeft, Wrench, Play, CheckCircle, FileText, ExternalLink } from 'lucide-react';

const STATUT_BADGE = {
  RECU: 'bg-blue-100 text-blue-800',
  EN_COURS: 'bg-orange-100 text-orange-800',
  TERMINE: 'bg-green-100 text-green-800',
  REMIS_CLIENT: 'bg-gray-100 text-gray-700'
};

export default function SAVDetailPage({ savId, onBack }) {
  const [sav, setSav] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/sav/dossiers/${savId}`);
      const data = await res.json();
      if (res.ok) setSav(data);
      else setError(data.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [savId]);

  useEffect(() => { load(); }, [load]);

  const lancerAtelier = async () => {
    if (!window.confirm('Lancer ce SAV en atelier ? Un OF sera cree.')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/sav/dossiers/${savId}/lancer-atelier`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Erreur'); return; }
      await load();
    } finally { setBusy(false); }
  };

  const marquerRemis = async () => {
    if (!window.confirm('Marquer ce SAV comme remis au client ?')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/sav/dossiers/${savId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: 'REMIS_CLIENT' })
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Erreur');
        return;
      }
      await load();
    } finally { setBusy(false); }
  };

  const downloadBon = () => {
    window.open(`${API_URL}/sav/dossiers/${savId}/bon.pdf`, '_blank');
  };

  if (loading) return <div className="p-6">Chargement...</div>;
  if (!sav) return <div className="p-6 text-red-600">{error || 'Introuvable'}</div>;

  const clientLabel = sav.client_nom ||
    (sav.client_table_nom ? `${sav.client_table_nom} ${sav.client_table_prenom || ''}`.trim() : '-');
  const tel = sav.client_telephone || sav.client_table_tel || '-';

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <button onClick={onBack} className="mb-4 text-blue-600 hover:underline flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Retour liste
      </button>

      <div className="bg-white rounded-xl shadow p-6 mb-4">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold font-mono text-blue-700">{sav.numero_sav}</h1>
            <div className="text-sm text-gray-500 mt-1">
              {sav.type_sav} - Recu le {sav.date_reception ? new Date(sav.date_reception).toLocaleString('fr-FR') : '-'}
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STATUT_BADGE[sav.statut] || 'bg-gray-100'}`}>
            {sav.statut}
          </span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded mb-3 text-sm">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="border rounded-lg p-3">
            <div className="text-xs text-gray-500 font-semibold mb-2">ARTICLE</div>
            <div className="font-medium">{sav.bijou_nom || '-'}</div>
            <div className="text-sm text-gray-600 font-mono">
              {sav.reference_article || sav.id_produit || ''}
              {sav.is_sav_external ? ' (externe)' : ''}
            </div>
            {sav.poids && <div className="text-sm text-gray-600">Poids: {sav.poids} g</div>}
            {sav.matiere && <div className="text-sm text-gray-600">Matiere: {sav.matiere}</div>}
          </div>

          <div className="border rounded-lg p-3">
            <div className="text-xs text-gray-500 font-semibold mb-2">CLIENT</div>
            <div className="font-medium">{clientLabel}</div>
            <div className="text-sm text-gray-600">Tel: {tel}</div>
            {sav.date_promise && (
              <div className="text-sm text-orange-600 mt-2">Promesse: {sav.date_promise}</div>
            )}
          </div>
        </div>

        <div className="border rounded-lg p-3 mb-4">
          <div className="text-xs text-gray-500 font-semibold mb-2">TRAVAIL DEMANDE</div>
          <div className="text-sm whitespace-pre-wrap">{sav.description_travail || '-'}</div>
        </div>

        {/* Tarif - masque pour ouvrier (backend renvoie null) */}
        {sav.tarif_type && (
          <div className="border rounded-lg p-3 mb-4 bg-yellow-50">
            <div className="text-xs text-gray-500 font-semibold mb-2">TARIF</div>
            <div className="text-sm">
              <span className="font-semibold">{sav.tarif_type}</span>
              {sav.tarif_type === 'PAYANT' && sav.prix !== null && (
                <span className="ml-2 font-mono">{parseFloat(sav.prix).toLocaleString('fr-FR')} DH</span>
              )}
            </div>
          </div>
        )}

        {sav.notes_admin && (
          <div className="border rounded-lg p-3 mb-4">
            <div className="text-xs text-gray-500 font-semibold mb-2">NOTES ADMIN</div>
            <div className="text-sm whitespace-pre-wrap">{sav.notes_admin}</div>
          </div>
        )}

        {/* Workflow */}
        <div className="border-t pt-4 mt-4 flex flex-wrap gap-2">
          {sav.statut === 'RECU' && (
            <button onClick={lancerAtelier} disabled={busy}
                    className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center gap-2 disabled:opacity-50">
              <Play className="w-4 h-4" /> Lancer en atelier
            </button>
          )}
          {sav.of_numero && (
            <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded flex items-center gap-2 text-sm">
              <Wrench className="w-4 h-4 text-blue-600" />
              OF lie: <span className="font-mono font-bold">{sav.of_numero}</span>
              <span className="text-gray-500">({sav.of_statut})</span>
            </div>
          )}
          {sav.statut === 'TERMINE' && (
            <button onClick={marquerRemis} disabled={busy}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2 disabled:opacity-50">
              <CheckCircle className="w-4 h-4" /> Marquer remis client
            </button>
          )}
          <button onClick={downloadBon}
                  className="px-4 py-2 border rounded hover:bg-gray-50 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Bon de reception PDF
          </button>
        </div>
      </div>

      {/* Photos */}
      {sav.photos && sav.photos.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold mb-3">Photos ({sav.photos.length})</h3>
          <div className="grid grid-cols-4 gap-3">
            {sav.photos.map(p => (
              <div key={p.id} className="border rounded overflow-hidden">
                <img src={p.url} alt={p.type_photo} className="w-full h-32 object-cover" />
                <div className="text-xs text-center py-1 bg-gray-50">{p.type_photo}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
